// Runtime values come from window.__RUNTIME_CONFIG__, set by the non-bundled
// public/environment.js (rewritten per-env at container start by
// process-sentinel's reactEnv injection, configured in docker/sentinel.json).
// Every value there is ALWAYS a string — env vars are strings, so "" is what an
// unset var looks like; null/undefined never appear.
//
// Fallback chain per var: runtime value -> build-time VITE_* -> domain-based default.
// The chain MUST use `||`, never `??`: "" is falsy but not nullish, so `??` would
// accept the empty string as a real value and every URL below would silently
// become "". Keep each import.meta.env.VITE_* access LITERAL — Vite only inlines
// static property access, so a computed import.meta.env[key] would break the
// build-time fallback. Adding a runtime var: add a line to public/environment.js
// AND an explicit export below.
//
// Both halves of each chain use the SAME name (VITE_API_URL, VITE_ACCOUNT_API_URL,
// …), so a variable is spelled one way everywhere: ui/.env for dev, Doppler and
// reactEnvVars for the container. The runtime half is a plain object key that never
// passes through Vite — it just keeps the prefix so there is no second name to
// mismatch. A key missing from reactEnvVars in docker/sentinel.json is never
// injected and stays "", falling through to the build-time value below.
declare global {
  interface Window {
    __RUNTIME_CONFIG__?: Record<string, string | undefined>;
  }
}

const runtimeConfig = window.__RUNTIME_CONFIG__ || {};

// The services gateway shares the Toolbox host's domain: https://services.backatyou{-stg,-dev}.com/{service}.
// `npm run dev` (any host: localhost, 127.0.0.1, a LAN IP, a tunnel) uses staging; any other
// unrecognised host gets production.
const SERVICES_DOMAIN =
  /backatyou(?:-stg|-dev)?\.com$/.exec(window.location.hostname)?.[0] ??
  (import.meta.env.DEV ? 'backatyou-stg.com' : 'backatyou.com');
const SERVICES_ORIGIN = `https://services.${SERVICES_DOMAIN}`;
export const TOOLBOX_API_URL = runtimeConfig.VITE_API_URL || import.meta.env.VITE_API_URL || '/api';
export const ACCOUNT_API_URL =
  runtimeConfig.VITE_ACCOUNT_API_URL ||
  import.meta.env.VITE_ACCOUNT_API_URL ||
  `${SERVICES_ORIGIN}/account`;
export const LISTING_API_URL =
  runtimeConfig.VITE_LISTING_API_URL ||
  import.meta.env.VITE_LISTING_API_URL ||
  `${SERVICES_ORIGIN}/listings`;
// The AI service is called directly from the browser (no proxy), so a local dev
// server points at the service-ai dev port instead of the gateway.
export const AI_API_URL =
  runtimeConfig.VITE_AI_API_URL ||
  import.meta.env.VITE_AI_API_URL ||
  (import.meta.env.DEV ? 'http://localhost:30331/v1' : `${SERVICES_ORIGIN}/ai/v1`);
// service-notification is called directly from the browser, on its dev port locally.
export const NOTIFICATION_API_URL =
  runtimeConfig.VITE_NOTIFICATION_API_URL ||
  import.meta.env.VITE_NOTIFICATION_API_URL ||
  (import.meta.env.DEV ? 'http://localhost:30151/v1' : `${SERVICES_ORIGIN}/notification/v1`);
// Build-time ONLY — deliberately not in the runtime chain above. The version is a
// property of the image, not of the environment it runs in, so it is baked by the
// ui-build stage (ENV VITE_BUILD_VERSION=${BUILD_VERSION}) and must never be
// runtime-injectable: a Doppler value could then disagree with the running code.
// Keep it out of public/environment.js and out of reactEnvVars.
export const BUILD_VERSION = import.meta.env.VITE_BUILD_VERSION || '0.0.0-local';
